/**
 * The frame: navigation, and the four states between "signed in" and "the app".
 *
 * The first-run copy is the load-bearing part. A user who sees one of these cards is
 * already having a bad minute, and the difference between "sign in again" and "signing in
 * again will not help" is the difference between a fix and a loop.
 */
export const nav = {
  skipToContent: 'Skip to content',
  primary: 'Primary',
  menu: 'Menu',
  closeMenu: 'Close menu',
  /** The current container's pages, under the switcher. */
  containerItems: [
    { section: 'overview', label: 'Overview' },
    { section: 'keys', label: 'Encryption keys' },
    { section: 'api-tokens', label: 'API tokens' },
    { section: 'firewalls', label: 'Firewalls' },
    { section: 'usage', label: 'Usage' },
  ],
  /** The tenant's, the same whichever container is open. Billing is present on every
   *  deployment, billing or not: /billing itself says when the API has it switched off. */
  tenantItems: [
    { to: '/containers', label: 'Containers' },
    { to: '/billing', label: 'Billing' },
    { to: '/settings', label: 'Settings' },
  ],
  /** The second group's visible label, which also names the group for a screen reader. */
  tenantGroup: 'Tenant',
} as const;

export const shell = {
  /**
   * Shown in the nav footer when the tenant name is just the account's own identifier.
   * Capitalised because it always starts its line.
   */
  yourTenant: 'Your tenant',
  signOut: 'Sign out',
  /** The browser tab's title, the page's own first, so tabs side by side can be told apart. */
  documentTitle: (page: string) => `${page} · rypt`,
  /** Beside a load failure, for the reader to try the read again now. */
  retry: 'Retry',
  /** The nav footer's identity when Auth0 gave neither an email nor a name. */
  userId: (id: string) => `user ${id}`,
} as const;

export const splash = {
  /** Nothing renders for this long: a flash of "loading" for a 200 ms call is worse than silence. */
  quietMs: 400,
  /** After this long the wait is abnormal enough to deserve an explanation. */
  explainAfterMs: 4_000,
  opening: 'Opening your tenant.',
  stillOpening:
    'Still opening. This is the first request of the session, so it also starts rypt’s connection to Google Cloud KMS.',
} as const;

/** The retry card's attempt count as a word: the retry policy makes at most five. */
const ATTEMPTS: Record<number, string> = { 1: 'one', 2: 'two', 3: 'three', 4: 'four', 5: 'five' };
const counted = (n: number, noun: string) => `${ATTEMPTS[n] ?? String(n)} ${noun}${n === 1 ? '' : 's'}`;

/**
 * The retry card's words for `unavailable`. A transport failure takes the error table's
 * own title, `transportCause` while it retries, and `transportExhausted` once it stops.
 */
export const retrying = {
  title: 'rypt is shedding load',
  /**
   * `unavailable` is rypt failing to check the token, not the token failing. Say so.
   * Two parts: the cause, which is announced once and stays the same through every
   * attempt, and the countdown, which ticks every second beside it and is not.
   */
  cause: 'unavailable: rypt cannot reach its identity provider. Your sign-in is fine — rypt’s own check of it is not.',
  countdown: (seconds: number) => `Retrying in ${seconds}s.`,
  /** Once the read has failed for good, with the attempts it made: five for unavailable. */
  exhausted: (attempts: number) =>
    `unavailable: ${counted(attempts, 'attempt')}, ${counted(attempts, 'failure')}. rypt still cannot reach its identity provider. Retry, or come back in a few minutes — nothing is wrong on your side and nothing needs redoing.`,
  /**
   * The cause while a request that got no answer is retried. Not the error table's
   * transport line, which ends "Nothing was sent twice.": the banner's reassurance about a
   * failed write, and false on a card about to send the same request again.
   */
  transportCause: 'The request never got an answer: the network is down, or the browser refused the response.',
  /**
   * Once a request that got no answer has failed for good, with the attempts it made:
   * three for transport. No identity provider and no "nothing is wrong on your side":
   * nothing answered, so nothing is known about rypt. From here a dead network and
   * something on the way refusing the request look the same, an edge's 502 that carries
   * no CORS headers among them, so the line names both, and a fix for each.
   */
  transportExhausted: (attempts: number) =>
    `The request never got an answer: ${counted(attempts, 'attempt')}, ${counted(attempts, 'failure')}. The network is down, or something on the way to rypt is refusing the request. Check the connection, or come back in a few minutes, then Retry.`,
  retryNow: 'Retry now',
  /**
   * In the live line of the card whose Retry was pressed, the retry card's or the catch-all
   * card's, while the read that press sent is out and nothing has answered it. The line
   * changes again when the answer comes, which is how the outcome is announced. Never
   * while the browser is offline: then nothing is sent, and the card keeps its words.
   */
  now: 'Retrying now.',
} as const;

/**
 * Auth0 itself refused or failed the sign-in, before rypt saw a token. Not the breaker's
 * card: nothing was refused twice. The body is landing's auth0Error(), which knows the
 * fix for access_denied and for an unverified email.
 */
export const auth0Failed = {
  title: 'Auth0 stopped the sign-in',
  action: 'Sign in',
} as const;

export const signInAgain = {
  title: 'This session was refused twice',
  /** Reached only when the breaker has already stopped one automatic redirect. */
  line: 'unauthorized: rypt rejected this session’s token twice. Sign in again.',
  action: 'Sign in',
} as const;

export const noTenant = {
  title: 'This account has no tenant',
  /** The body is ERROR_BEHAVIOUR.no_tenant.copy(e); only the action belongs here.
   *  Never "Sign in again": re-authenticating produces the same account with the same
   *  missing membership, and offering it would send someone round that loop forever. */
  action: 'Sign out',
} as const;

export const routeError = {
  notFoundTitle: 'No such page',
  notFoundBody: 'That address is not part of the console.',
  backToOverview: 'Go to Overview',
} as const;
